import { type FC, useMemo } from "react";

import { InlineMapper } from "./inline-mapper";
import type { ArrayMapping, InlineMapperProps } from "../types";
import { type ColumnOption, useFormkitContext } from "../formkit-context";

type ArrayInlineMapperProps = Omit<InlineMapperProps, "value" | "type"> & {
  value: ArrayMapping;
};

function findOptionByValue(
  groups: ColumnOption[] | undefined,
  value: string | undefined,
): ColumnOption | undefined {
  // Event-schema suggestions come in as grouped `ColumnOption[]` and array fields carry
  // nested `elementOptions` (e.g. selecting `properties.users` should surface `id`, `active`, etc).
  // This helper finds the *selected* array field option by its `value` so we can scope the
  // inline mapper to that array element's properties.
  if (!groups || !value) return undefined;
  for (const group of groups) {
    for (const opt of group.options ?? []) {
      if (opt.value === value) return opt;
    }
  }
  return undefined;
}

export const ArrayInlineMapper: FC<Readonly<ArrayInlineMapperProps>> = (
  props,
) => {
  const {
    jsonColumnProperties,
    value,
    enabledNestedInlineMapper,
    columnOptionsOverride,
  } = props;
  const { model, columns: contextColumns } = useFormkitContext();
  const isEventModel = Boolean(model?.isEventModel);
  const scopedColumns = columnOptionsOverride ?? contextColumns;
  const { allColumnsProps } = jsonColumnProperties;

  const elementColumnsOverride: ColumnOption[] | undefined = useMemo(() => {
    if (!isEventModel) {
      const properties = value.from ? allColumnsProps?.[value.from] : undefined;
      return (
        properties &&
        Array.from(properties, (property) => ({
          label: property,
          value: property,
          type: "array_property",
        }))
      );
    }
    const selected = findOptionByValue(scopedColumns, value.from);
    const elementOptions = selected?.elementOptions ?? [];
    return [
      {
        label: "Array element properties",
        value: "",
        type: null,
        options: elementOptions,
      },
    ];
  }, [allColumnsProps, isEventModel, scopedColumns, value.from]);

  return (
    <InlineMapper
      {...props}
      enabledNestedInlineMapper={enabledNestedInlineMapper}
      columnOptionsOverride={elementColumnsOverride}
      overwriteColumnsWithArrayProps={!isEventModel}
      type="array"
    />
  );
};
